import * as XLSX from 'xlsx';
import { VALID_UNITS_OF_MEASURE, VALID_RAW_MATERIAL_CATEGORIES } from './validator';

export interface Category {
  id: string;
  name: string;
}

export interface Supplier {
  id: string;
  name: string;
}

export interface TemplateOptions {
  categories?: Category[];
  suppliers?: Supplier[];
  includeExamples?: boolean;
}

// Product sheet headers and example data
const PRODUCT_HEADERS = [
  'name',
  'sku',
  'barcode',
  'price',
  'cost',
  'tax_rate',
  'category',
  'description',
  'track_inventory',
  'low_stock_threshold',
  'par_level',
  'initial_stock',
  'modifier_groups',
];

const PRODUCT_EXAMPLES = [
  ['Espresso', 'ESP-001', '123456789012', 4.50, 1.20, 8.25, 'Coffee', 'Rich single shot espresso', 'TRUE', 10, 50, 100, 'Size;Temperature'],
  ['Cappuccino', 'CAP-001', '123456789013', 5.50, 1.80, 8.25, 'Coffee', 'Classic Italian cappuccino', 'TRUE', 10, 50, 100, 'Size'],
  ['Chocolate Croissant', 'CRO-001', '123456789014', 3.50, 1.00, 0, 'Pastries', 'Buttery croissant with chocolate', 'TRUE', 5, 20, 50, ''],
];

// Raw materials sheet headers and example data
const RAW_MATERIAL_HEADERS = [
  'name',
  'sku',
  'barcode',
  'description',
  'unit_of_measure',
  'cost_per_unit',
  'supplier_name',
  'supplier_item_code',
  'category',
  'reorder_point',
  'reorder_quantity',
  'initial_stock',
];

const RAW_MATERIAL_EXAMPLES = [
  ['Coffee Beans (Arabica)', 'RAW-COFFEE-001', '', 'Premium arabica beans', 'kg', 25.00, 'Coffee Supplier Co', 'SUP-ARB-001', 'raw_ingredient', 5, 20, 50],
  ['Milk (Whole)', 'RAW-MILK-001', '', 'Fresh whole milk', 'liter', 1.50, 'Dairy Farm Inc', 'DF-MILK-W', 'raw_ingredient', 10, 30, 100],
  ['Paper Cups (12oz)', 'RAW-CUP-001', '', 'Disposable paper cups', 'case', 45.00, 'Packaging Plus', 'PP-CUP-12', 'packaging', 2, 5, 10],
];

// Modifiers sheet headers and example data
const MODIFIER_HEADERS = [
  'group_name',
  'option_name',
  'price_adjustment',
  'is_default',
  'min_selections',
  'max_selections',
  'is_required',
  'sort_order',
];

const MODIFIER_EXAMPLES = [
  ['Size', 'Small', -0.50, 'FALSE', 1, 1, 'TRUE', 1],
  ['Size', 'Medium', 0, 'TRUE', 1, 1, 'TRUE', 2],
  ['Size', 'Large', 0.50, 'FALSE', 1, 1, 'TRUE', 3],
  ['Temperature', 'Hot', 0, 'TRUE', 1, 1, 'TRUE', 1],
  ['Temperature', 'Iced', 0, 'FALSE', 1, 1, 'TRUE', 2],
  ['Extras', 'Extra Shot', 0.75, 'FALSE', 0, 3, 'FALSE', 1],
  ['Extras', 'Oat Milk', 0.60, 'FALSE', 0, 3, 'FALSE', 2],
];

// Categories sheet headers and example data
const CATEGORY_HEADERS = [
  'name',
  'description',
  'parent_category',
  'sort_order',
];

const CATEGORY_EXAMPLES = [
  ['Coffee', 'Hot and cold coffee drinks', '', 1],
  ['Espresso Drinks', 'Espresso-based beverages', 'Coffee', 1],
  ['Pastries', 'Fresh baked goods', '', 2],
  ['Croissants', 'Buttery French pastries', 'Pastries', 1],
];

// Instructions content
function getInstructionsContent(options: TemplateOptions): string[][] {
  const instructions = [
    ['BLAZEPOS PRODUCT IMPORT TEMPLATE'],
    [''],
    ['GENERAL INSTRUCTIONS'],
    ['- Fill in the data in each sheet according to the column headers'],
    ['- Required fields are marked with an asterisk (*) in the descriptions below'],
    ['- Do not modify the header row (first row) in each sheet'],
    ['- Empty rows will be skipped'],
    ['- Save the file as .xlsx or export as .csv for import'],
    [''],
    ['PRODUCTS SHEET'],
    ['- name* : Product name (required)'],
    ['- sku* : Unique product identifier, used to match existing products (required)'],
    ['- barcode : Product barcode/UPC (optional)'],
    ['- price* : Selling price (required)'],
    ['- cost : Cost price (optional)'],
    ['- tax_rate : Tax percentage, e.g., 8.25 for 8.25% (optional)'],
    ['- category : Category name - will be created if it does not exist (optional)'],
    ['- description : Product description (optional)'],
    ['- track_inventory : TRUE or FALSE (optional, defaults to TRUE)'],
    ['- low_stock_threshold : Stock level that triggers low stock alert (optional)'],
    ['- par_level : Target stock level (optional)'],
    ['- initial_stock : Starting inventory quantity (optional)'],
    ['- modifier_groups : Semicolon-separated list of modifier group names (optional)'],
    [''],
    ['RAW MATERIALS SHEET'],
    ['- name* : Item name (required)'],
    ['- sku : Unique SKU for matching (optional)'],
    ['- barcode : Item barcode (optional)'],
    ['- description : Item description (optional)'],
    [`- unit_of_measure* : Must be one of: ${VALID_UNITS_OF_MEASURE.join(', ')} (required)`],
    ['- cost_per_unit : Cost per unit (optional)'],
    ['- supplier_name : Supplier name - must match existing supplier (optional)'],
    ['- supplier_item_code : Supplier\'s item code/SKU (optional)'],
    [`- category : Must be one of: ${VALID_RAW_MATERIAL_CATEGORIES.join(', ')} (optional)`],
    ['- reorder_point : Stock level that triggers reorder alert (optional)'],
    ['- reorder_quantity : Suggested reorder quantity (optional)'],
    ['- initial_stock : Starting inventory quantity (optional)'],
    [''],
    ['MODIFIERS SHEET'],
    ['- group_name* : Name of the modifier group (required)'],
    ['- option_name* : Name of the modifier option (required)'],
    ['- price_adjustment : Price change when option is selected (optional, can be negative)'],
    ['- is_default : TRUE or FALSE - whether this option is selected by default (optional)'],
    ['- min_selections : Minimum number of options customer must select in this group (optional)'],
    ['- max_selections : Maximum number of options customer can select in this group (optional)'],
    ['- is_required : TRUE or FALSE - whether selection is required (optional)'],
    ['- sort_order : Display order within the group (optional)'],
    [''],
    ['CATEGORIES SHEET'],
    ['- name* : Category name (required)'],
    ['- description : Category description (optional)'],
    ['- parent_category : Name of parent category for nesting (optional)'],
    ['- sort_order : Display order (optional)'],
    [''],
    ['TIPS'],
    ['- For updating existing products, use the same SKU'],
    ['- Categories will be automatically created if they don\'t exist'],
    ['- Suppliers must exist before import - raw materials will be imported without supplier if not found'],
    ['- Modifier groups with the same name will have options merged'],
    [''],
  ];

  // Add existing categories if provided
  if (options.categories && options.categories.length > 0) {
    instructions.push(['EXISTING CATEGORIES']);
    options.categories.forEach(cat => {
      instructions.push([`- ${cat.name}`]);
    });
    instructions.push(['']);
  }

  // Add existing suppliers if provided
  if (options.suppliers && options.suppliers.length > 0) {
    instructions.push(['EXISTING SUPPLIERS']);
    options.suppliers.forEach(sup => {
      instructions.push([`- ${sup.name}`]);
    });
    instructions.push(['']);
  }

  return instructions;
}

// Generate Excel template
export function generateImportTemplate(options: TemplateOptions = {}): Blob {
  const workbook = XLSX.utils.book_new();

  // Products sheet
  const productsData: (string | number | boolean)[][] = [PRODUCT_HEADERS];
  if (options.includeExamples !== false) {
    productsData.push(...PRODUCT_EXAMPLES);
  }
  const productsSheet = XLSX.utils.aoa_to_sheet(productsData);
  setColumnWidths(productsSheet, [20, 15, 15, 10, 10, 10, 15, 30, 15, 18, 12, 12, 25]);
  XLSX.utils.book_append_sheet(workbook, productsSheet, 'Products');

  // Raw Materials sheet
  const rawMaterialsData: (string | number | boolean)[][] = [RAW_MATERIAL_HEADERS];
  if (options.includeExamples !== false) {
    rawMaterialsData.push(...RAW_MATERIAL_EXAMPLES);
  }
  const rawMaterialsSheet = XLSX.utils.aoa_to_sheet(rawMaterialsData);
  setColumnWidths(rawMaterialsSheet, [25, 15, 15, 25, 15, 12, 20, 18, 15, 12, 15, 12]);
  XLSX.utils.book_append_sheet(workbook, rawMaterialsSheet, 'Raw Materials');

  // Modifiers sheet
  const modifiersData: (string | number | boolean)[][] = [MODIFIER_HEADERS];
  if (options.includeExamples !== false) {
    modifiersData.push(...MODIFIER_EXAMPLES);
  }
  const modifiersSheet = XLSX.utils.aoa_to_sheet(modifiersData);
  setColumnWidths(modifiersSheet, [15, 15, 15, 12, 15, 15, 12, 12]);
  XLSX.utils.book_append_sheet(workbook, modifiersSheet, 'Modifiers');

  // Categories sheet
  const categoriesData: (string | number | boolean)[][] = [CATEGORY_HEADERS];
  if (options.includeExamples !== false) {
    categoriesData.push(...CATEGORY_EXAMPLES);
  }
  const categoriesSheet = XLSX.utils.aoa_to_sheet(categoriesData);
  setColumnWidths(categoriesSheet, [20, 30, 20, 12]);
  XLSX.utils.book_append_sheet(workbook, categoriesSheet, 'Categories');

  // Instructions sheet
  const instructionsData = getInstructionsContent(options);
  const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionsData);
  setColumnWidths(instructionsSheet, [80]);
  XLSX.utils.book_append_sheet(workbook, instructionsSheet, 'Instructions');

  // Generate blob
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

// Generate CSV template for a specific sheet type
export function generateCSVTemplate(
  sheetType: 'products' | 'rawMaterials' | 'modifiers' | 'categories',
  options: TemplateOptions = {}
): Blob {
  let headers: string[];
  let examples: (string | number | boolean)[][] = [];

  switch (sheetType) {
    case 'products':
      headers = PRODUCT_HEADERS;
      if (options.includeExamples !== false) {
        examples = PRODUCT_EXAMPLES;
      }
      break;
    case 'rawMaterials':
      headers = RAW_MATERIAL_HEADERS;
      if (options.includeExamples !== false) {
        examples = RAW_MATERIAL_EXAMPLES;
      }
      break;
    case 'modifiers':
      headers = MODIFIER_HEADERS;
      if (options.includeExamples !== false) {
        examples = MODIFIER_EXAMPLES;
      }
      break;
    case 'categories':
      headers = CATEGORY_HEADERS;
      if (options.includeExamples !== false) {
        examples = CATEGORY_EXAMPLES;
      }
      break;
  }

  const rows = [headers.join(',')];
  examples.forEach(row => {
    const csvRow = row.map(cell => {
      if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))) {
        return `"${cell.replace(/"/g, '""')}"`;
      }
      return String(cell);
    }).join(',');
    rows.push(csvRow);
  });

  const csvContent = rows.join('\n');
  return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
}

// Helper to set column widths
function setColumnWidths(sheet: XLSX.WorkSheet, widths: number[]): void {
  sheet['!cols'] = widths.map(width => ({ wch: width }));
}

// Download helper
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Generate error report
export function generateErrorReport(
  errors: Array<{ row: number; field: string; message: string; sheet?: string }>
): Blob {
  const headers = ['Sheet', 'Row', 'Field', 'Error Message'];
  const rows = [headers];

  errors.forEach(error => {
    rows.push([
      error.sheet || 'Unknown',
      String(error.row),
      error.field,
      error.message,
    ]);
  });

  const workbook = XLSX.utils.book_new();
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  setColumnWidths(sheet, [15, 8, 20, 50]);
  XLSX.utils.book_append_sheet(workbook, sheet, 'Import Errors');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
